'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { LastSeenProductsDocument } from '@packages/gql/generated/shopping/LastSeenProductsDocument';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCardV2.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type LastSeenProductsRecoSliderProps = ProductsRecoSliderProps;

/* GraphQL */ `
  query LastSeenProducts($locale: String!) {
    lastSeenProducts(locale: $locale) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'lastSeenProducts';

/**
 * LastSeenProductsRecoSlider component description displayed in storybook
 * */
export const LastSeenProductsRecoSlider: FC<LastSeenProductsRecoSliderProps> = ({
  backgroundColor,
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  title,
}) => {
  const { jwtToken } = useSession();
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const { locale } = useI18n();

  const [{ data, error }] = useQuery({
    query: LastSeenProductsDocument,
    variables: { locale },
    context: useMemo(
      () => ({
        suspense: false,
        fetchOptions: { headers: { 'x-ec-token': jwtToken!, 'x-locale': locale } },
      }),
      [jwtToken, locale],
    ),
    pause: (fetchOnThreshold ? !isInThreshold : false) || !jwtToken,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.lastSeenProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.lastSeenProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.lastSeenProducts.title || title}
      backgroundColor={backgroundColor}
      loadingState={!filteredRecommendations}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          // pageTemplate={pageTemplate}
          // elementIndex={elementIndex}
          // position={idx}
          // productsLength={filteredRecommendations.length}
          // rdeType={RDE_TYPE}
        />
      ))}
    </RecoCarousel>
  );
};
